import { Card, Grid } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { ContactList } from '../contacts/ContactList';
import ExpandableCard from '../../../../components/common/ExpandableCard';
import CaseCareTeamGrid from 'src/careflow/care-coordination/case/detail/components/CaseCareTeamGrid';
import { CarePlanWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { useCarePlans, useCareTeams } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { CareTeamWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { GroupList } from 'src/careflow/care-coordination/patient/groups';
import GeneralPractitionerList from 'src/careflow/care-coordination/patient/general-practitioner/GeneralPractitionerList';
import { RelatedPersonList } from 'src/careflow/care-coordination/patient/related-person/RelatedPersonList';
import { PatientDetails } from 'src/careflow/care-coordination/patient/detail/patientDetails.type';

export default function PatientGroups({ patient }: PatientDetails) {
  const { i18n } = useLocales();

  const [
    carePlan,
    { refresh: refreshCarePlan },
  ] = useCarePlans({ filter: { subject: patient.id }, map: CarePlanWrapper });
 
  const [relatedPersonsOpen, setRelatedPersonsOpen] = useState(false);

  const [careTeams, { refresh: refreshCareTeams }] = useCareTeams({
    map: CareTeamWrapper,
    filter: {
      _id: carePlan[0]?.getCareTeamIds().join(','),
    },
    autofetch: !!carePlan[0]?.getCareTeamIds().length,
  });

  const refreshEntities = useCallback(() => {
    refreshCarePlan();
  }, []);
 
  const memoizedRefreshCareTeam = useCallback((careTeam) => {
    refreshCareTeams();
  }, []);

  const careTeamPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.CARE_TEAMS.ADD],
      }),
      isAllowedToEdit: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.CARE_TEAMS.EDIT],
      }),
      members: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.CARE_TEAMS.MEMBERS.ADD],
        }),
        isAllowedToDelete: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.CARE_TEAMS.MEMBERS.DELETE],
        }),
        isAllowedToEdit: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.CARE_TEAMS.MEMBERS.EDIT],
        }),
      },
    }),
    []
  );

  return (
    <Card sx={{ p: 2, boxShadow: 'none' }}>
      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.GROUPS.VIEW] }) && (
        <ExpandableCard title={i18n('patients.details.groups.title', 'crs')} sxProp={{ m: 1 }}>
          <GroupList patient={patient} PATH_PERMISIONS='PATIENT'/>
        </ExpandableCard>
      )}

      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.GENERAL_PRACTITIONER.VIEW] }) && (
        <ExpandableCard
          title={i18n('patients.generalPractitioners.title', 'crs')}
          sxProp={{ m: 1 }}
        >
          <GeneralPractitionerList patient={patient} />
        </ExpandableCard>
      )}

      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CONTACTS.VIEW] }) && (
        <ExpandableCard title={i18n('patients.details.contacts.title', 'crs')} sxProp={{ m: 1 }}>
          <ContactList patient={patient} />
        </ExpandableCard>
      )}

      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.RELATED_PERSONS.VIEW] }) && (
        <ExpandableCard
          title={i18n('patients.details.relatedPersons.title', 'crs')}
          sxProp={{ m: 1 }}
        >
          <RelatedPersonList
            patient={patient}
            open={relatedPersonsOpen}
            setOpen={setRelatedPersonsOpen}
          />
        </ExpandableCard>
      )}

      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CARE_TEAMS.VIEW] }) && (
        <Grid sx={{ p: 1, boxShadow: 'none' }} item xs={12}>
          <CaseCareTeamGrid
            carePlan={carePlan[0]}
            careTeams={careTeams}
            patient={patient as WrappedPatient}
            onSuccessfulCreation={refreshEntities}
            onSuccessfulEdit={memoizedRefreshCareTeam}
            onMemberUpdateSuccess={memoizedRefreshCareTeam}
            openCollapseExternal={false}
            permissions={careTeamPermissions}
            showAddButton={false}
            title={i18n('case.details.careteams.title', 'crs')}
          />
        </Grid>
      )}
    </Card>
  );
}
